/**
 * The book table's hierarchy: one lazily-loaded level per expanded row.
 *
 * Accessibility trade-off, stated here rather than discovered later: this is a
 * plain `<table>`, NOT `role="treegrid"`. A full treegrid means arrow-key
 * navigation across every cell, and a half-implemented one is worse than none —
 * a screen reader announces a grid contract the table does not honour. Instead
 * each expander is a real `<button aria-expanded>` whose accessible name
 * carries the whole context ("Expand mn7's book — 3 accounts beneath, worst
 * case ₹4.2L"), each branch row states its parent in screen-reader-only text,
 * and every row is reachable by Tab through its link and its expander.
 */

import { Fragment, useEffect, type CSSProperties } from 'react'
import { Button, Chip, Icon, Skeleton } from '@w7/ui'
import { AccountCell } from '@/app/AppShell'
import { Money } from '@/app/bits'
import { useMarketBookBranch } from '@/app/queries'
import { EMPTY, count, moneyCompact } from '@/lib/format'
import { ROLE_LABEL, type BookBranchRow } from '@/data/types'

/** Skeletons while a branch loads, capped so a 40-child master does not paint a wall. */
const MAX_SKELETON_ROWS = 8

export interface BranchProps {
  marketId: string
  basis: 'my' | 'total'
  parentId: string
  /** Direct children this account is known to have — the skeleton count. */
  expectedChildren: number
  depth: number
  selectionCount: number
  /** Username of the account these rows sit under, for the sr-only "Under sm2". */
  parentName: string
  expanded: Set<string>
  onToggle: (accountId: string) => void
  /** Reports loaded rows up so Export book can write exactly what is on screen. */
  onLoaded: (parentId: string, rows: BookBranchRow[] | null) => void
}

export function BookBranch({
  marketId,
  basis,
  parentId,
  expectedChildren,
  depth,
  selectionCount,
  parentName,
  expanded,
  onToggle,
  onLoaded,
}: BranchProps) {
  const { data, isLoading, isError, error, refetch } = useMarketBookBranch(marketId, parentId, basis)
  const rows = data?.value ?? []
  const stale = data?.stale ?? false

  // A stale branch reports NOTHING upward, so Export book cannot write a figure
  // the table itself refuses to show.
  useEffect(() => {
    onLoaded(parentId, stale ? null : (data?.value ?? null))
  }, [onLoaded, parentId, data, stale])

  // Withdraw only on unmount — i.e. when the row is collapsed. Folding this
  // into the effect above would delete and re-add the branch on every poll.
  useEffect(() => () => onLoaded(parentId, null), [onLoaded, parentId])

  const groupId = `book-children-${parentId}`
  const span = selectionCount + 2

  if (isLoading) {
    return (
      <>
        {Array.from({ length: Math.max(1, Math.min(expectedChildren || 1, MAX_SKELETON_ROWS)) }).map(
          (_, i) => (
            <tr key={i} id={i === 0 ? groupId : undefined} className="w7-book-row">
              <th scope="row" className="w7-book-label">
                <span className="w7-book-indent" style={{ '--w7-depth': depth } as CSSVars}>
                  <Skeleton width={140} height={14} />
                </span>
              </th>
              <td className="w7-book-net">
                <Skeleton width={72} height={12} />
              </td>
              {Array.from({ length: selectionCount }).map((__, c) => (
                <td key={c}>
                  <Skeleton width={72} height={12} />
                </td>
              ))}
            </tr>
          ),
        )}
      </>
    )
  }

  if (isError) {
    return (
      <tr id={groupId} className="w7-mb-rowinfo">
        <th scope="row" className="w7-book-label">
          <span className="w7-book-indent" style={{ '--w7-depth': depth } as CSSVars}>
            Could not load
          </span>
        </th>
        <td colSpan={span - 1}>
          <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
            {(error as Error)?.message ?? 'That level of the tree could not be loaded.'}
            <Button variant="ghost" size="sm" icon="refresh" onClick={() => refetch()}>
              Retry
            </Button>
          </span>
        </td>
      </tr>
    )
  }

  if (rows.length === 0) {
    // Gated by `hasChildren`, so this should be unreachable. It renders a
    // sentence rather than nothing so an expander that opens onto emptiness is
    // a visible bug instead of a silent one.
    return (
      <tr id={groupId} className="w7-mb-rowinfo">
        <td colSpan={span}>Nothing beneath this account holds a position on this market.</td>
      </tr>
    )
  }

  return (
    <>
      {rows.map((row, index) => {
        const open = expanded.has(row.account.id)
        return (
          <Fragment key={row.account.id}>
            <BranchRow
              id={index === 0 ? groupId : undefined}
              row={row}
              depth={depth}
              parentName={parentName}
              stale={stale}
              expanded={open}
              onToggle={onToggle}
            />
            {open ? (
              <BookBranch
                marketId={marketId}
                basis={basis}
                parentId={row.account.id}
                expectedChildren={row.account.childCount}
                depth={depth + 1}
                selectionCount={selectionCount}
                parentName={row.account.username}
                expanded={expanded}
                onToggle={onToggle}
                onLoaded={onLoaded}
              />
            ) : null}
          </Fragment>
        )
      })}
    </>
  )
}

/* ------------------------------------------------------------------ rows -- */

/** `.w7-book-indent` reads `--w7-depth`, so the indent is one CSS rule rather
 *  than a per-row inline padding the next reader has to reverse-engineer. */
type CSSVars = CSSProperties & Record<'--w7-depth', number>

function BranchRow({
  id,
  row,
  depth,
  parentName,
  stale,
  expanded,
  onToggle,
}: {
  id?: string
  row: BookBranchRow
  depth: number
  parentName: string
  stale: boolean
  expanded: boolean
  onToggle: (accountId: string) => void
}) {
  const { account } = row

  return (
    <tr className="w7-book-row" data-kind="branch" id={id}>
      <th scope="row" className="w7-book-label">
        <span className="w7-book-indent" style={{ '--w7-depth': depth } as CSSVars}>
          <Expander
            row={row}
            expanded={expanded}
            onToggle={() => onToggle(account.id)}
            stale={stale}
          />
          <span style={{ minWidth: 0 }}>
            <AccountCell
              username={account.username}
              displayName={account.displayName}
              role={account.role}
              to={`/member/${account.id}`}
              meta={
                <span className="w7-mb-branchmeta">
                  {count(row.betCount)} {row.betCount === 1 ? 'bet' : 'bets'} ·{' '}
                  {count(row.memberCount)}{' '}
                  {row.memberCount === 1 ? ROLE_LABEL.player : `${ROLE_LABEL.player}s`}
                </span>
              }
            />
            <span className="w7-sr">Under {parentName}</span>
          </span>
        </span>
      </th>
      <BookCells positions={row.positions} worstCase={row.worstCase} stale={stale} />
    </tr>
  )
}

/**
 * The expander.
 *
 * Rendered only when a child actually holds a position here (`hasChildren`), so
 * there is no disabled twisty that opens onto nothing. The spacer keeps the
 * usernames of leaf rows aligned with the ones that carry a control.
 */
function Expander({
  row,
  expanded,
  onToggle,
  stale,
}: {
  row: BookBranchRow
  expanded: boolean
  onToggle: () => void
  stale: boolean
}) {
  if (!row.hasChildren) return <span className="w7-book-expander" aria-hidden="true" />

  const beneath = `${count(row.account.childCount)} ${
    row.account.childCount === 1 ? 'account' : 'accounts'
  } beneath`
  const worst = stale ? 'worst case unavailable' : `worst case ${moneyCompact(row.worstCase)}`

  return (
    <button
      type="button"
      className="w7-book-expander"
      aria-expanded={expanded}
      aria-controls={expanded ? `book-children-${row.account.id}` : undefined}
      onClick={onToggle}
    >
      <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={15} />
      <span className="w7-sr">
        {expanded ? 'Collapse' : 'Expand'} {row.account.username}&rsquo;s book — {beneath}, {worst}
      </span>
    </button>
  )
}

/**
 * Net exposure + one cell per selection.
 *
 * A stale branch shows the em dash and a warn chip, never a stale number — the
 * rule holds one row deep exactly as it holds for the page.
 */
export function BookCells({
  positions,
  worstCase,
  stale,
}: {
  positions: { name: string; net: number }[]
  worstCase: number
  stale: boolean
}) {
  if (stale) {
    return (
      <>
        <td className="w7-book-net">
          <Chip size="sm" tone="warn">
            Unavailable
          </Chip>
        </td>
        {positions.map((position) => (
          <td key={position.name} className="w7-dim">
            {EMPTY}
          </td>
        ))}
      </>
    )
  }

  return (
    <>
      <td className="w7-book-net">
        <Money value={worstCase} signed />
      </td>
      {positions.map((position) => (
        <td key={position.name}>
          <Money value={position.net} signed />
        </td>
      ))}
    </>
  )
}

/** The root row's expander. Same control, but its context is "your book". */
export function RootExpander({
  expanded,
  onToggle,
  accountId,
  childCount,
  worstCase,
}: {
  expanded: boolean
  onToggle: () => void
  accountId: string
  childCount: number
  worstCase: number
}) {
  if (childCount === 0) return <span className="w7-book-expander" aria-hidden="true" />
  return (
    <button
      type="button"
      className="w7-book-expander"
      aria-expanded={expanded}
      aria-controls={expanded ? `book-children-${accountId}` : undefined}
      onClick={onToggle}
    >
      <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={15} />
      <span className="w7-sr">
        {expanded ? 'Collapse' : 'Expand'} your book — {count(childCount)}{' '}
        {childCount === 1 ? 'account' : 'accounts'} beneath, worst case {moneyCompact(worstCase)}
      </span>
    </button>
  )
}
